import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import { 
  ArrowLeft, Mail, MapPin, Users, 
  Briefcase, Handshake, Globe, Award 
} from 'lucide-react';

const CompanyPage = ({ type }) => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const theme = {
    primary: '#3ba9e1', secondary: '#0f172a', navBg: '#132238', navText: '#ffffff',
    bgLight: '#f8fafc', bgWhite: '#ffffff'
  };

  // --- COMPANY CONTENT DATABASE (Moved inside component for responsiveness) ---
  const PAGES = {
    about: {
      title: "About SiteACT",
      subtitle: "We are industry experts who learned to code, not coders trying to understand industry.",
      icon: <Users size={48} color="#2563eb" />,
      content: (
        <>
          <h3>Our Mission</h3>
          <p>Construction and industrial sites are complex, high-risk environments. Yet, the software used to manage them is often clunky, expensive, and designed for the office—not the field.</p>
          <p>We built SiteACT to be the operating system we wish we had: fast, mobile-enabled, and simple enough for a subcontractor to use without training.</p>
          
          <h3 style={{marginTop: '40px'}}>Our Story</h3>
          <p>Founded in London, SiteACT started when we realized that 40% of a Site Manager's day was spent chasing paper permits and managing excel tables. We set out to automate the friction of compliance so teams can focus on building.</p>
        </>
      )
    },
    careers: {
      title: "Join the Mission",
      subtitle: "Help us digitise the world's second-largest industry.",
      icon: <Briefcase size={48} color="#2563eb" />,
      content: (
        <>
          <h3>Current Openings</h3>
          
          <div style={{ padding: isMobile ? '20px' : '40px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', textAlign: 'center', marginTop: '30px', boxSizing: 'border-box' }}>
             <div style={{ marginBottom: '20px', display: 'inline-block', padding: '12px', borderRadius: '50%', background: '#eff6ff', color: '#2563eb' }}>
                <Award size={32} />
             </div>
             <h4 style={{ fontSize: '1.25rem', marginBottom: '15px', color: '#0f172a' }}>Fully Staffed (For Now)</h4>
             <p style={{ fontSize: '1.05rem', color: '#475569', marginBottom: '20px', maxWidth: '500px', margin: '0 auto 20px' }}>
                Our team positions have been filled for now, but we are growing fast! We are constantly scaling our engineering and sales teams.
             </p>
             <p style={{ color: '#64748b' }}>
                Check back in soon, or send your CV to <a href="mailto:careers@siteact.app" style={{ color: '#2563eb', fontWeight: 'bold', textDecoration: 'none' }}>careers@siteact.app</a> to be kept on file for future roles.
             </p>
          </div>
        </>
      )
    },
    partners: {
      title: "Partner Program",
      subtitle: "Grow your business by bringing digital compliance to your clients.",
      icon: <Handshake size={48} color="#2563eb" />,
      content: (
        <>
          <h3>Who we partner with</h3>
          {/* Responsive Grid: Stacks on mobile */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px', marginTop: '20px' }}>
            <div style={{ padding: '20px', background: '#f8fafc', borderRadius: '8px', boxSizing: 'border-box' }}>
              <strong>Safety Consultants</strong>
              <p style={{ fontSize: '0.9rem', color: '#64748b' }}>Digitise your clients' management systems. Recurring revenue share.</p>
            </div>
            <div style={{ padding: '20px', background: '#f8fafc', borderRadius: '8px', boxSizing: 'border-box' }}>
              <strong>Technology Resellers</strong>
              <p style={{ fontSize: '0.9rem', color: '#64748b' }}>Add a high-margin SaaS product to your portfolio. Recurring revenue share.</p>
            </div>
          </div>
          <div style={{ marginTop: '40px', padding: isMobile ? '20px' : '30px', background: '#eff6ff', borderRadius: '12px', textAlign: 'center', boxSizing: 'border-box' }}>
            <h4>Ready to partner?</h4>
            <p>Contact our partnership team at partners@siteact.app</p>
          </div>
        </>
      )
    },
    contact: {
      title: "Contact Sales",
      subtitle: "Enterprise questions? Custom integrations? We're here to help.",
      icon: <Mail size={48} color="#2563eb" />,
      content: (
        <>
          <div style={{ display: 'grid', gap: '30px', maxWidth: '600px', margin: '0 auto' }}>
            
            <div style={{ textAlign: 'center', padding: isMobile ? '20px' : '40px', border: '1px solid #e2e8f0', borderRadius: '16px', background: 'white', boxSizing: 'border-box' }}>
              <Mail size={32} color="#2563eb" style={{ marginBottom: '15px' }} />
              <h3 style={{ marginBottom: '10px' }}>Email Sales</h3>
              <p style={{ color: '#64748b', marginBottom: '20px' }}>For demos, pricing, and enterprise inquiries.</p>
              <a href="mailto:sales@siteact.app" style={{ fontSize: isMobile ? '1.1rem' : '1.25rem', fontWeight: 'bold', color: '#2563eb', textDecoration: 'none', wordBreak: 'break-all' }}>
                sales@siteact.app
              </a>
            </div>

            {/* Responsive Grid: Stacks on mobile */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px' }}>
              <div style={{ padding: '20px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', textAlign: 'center', boxSizing: 'border-box' }}>
                 <MapPin size={24} color="#64748b" style={{ marginBottom: '10px' }} />
                 <div style={{ fontWeight: 'bold' }}>London HQ</div>
                 <div style={{ fontSize: '0.9rem', color: '#64748b' }}>london@siteact.app</div>
              </div>
              <div style={{ padding: '20px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', textAlign: 'center', boxSizing: 'border-box' }}>
                 <Globe size={24} color="#64748b" style={{ marginBottom: '10px' }} />
                 <div style={{ fontWeight: 'bold' }}>Sofia</div>
                 <div style={{ fontSize: '0.9rem', color: '#64748b' }}>sofia@siteact.app</div>
              </div>
            </div>

          </div>
        </>
      )
    }
  };

  const page = PAGES[type];

  if (!page) return <div>Page not found</div>;

  return (
    <div style={{ fontFamily: '"Inter", sans-serif', backgroundColor: '#f8fafc', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Navbar theme={theme} />
      
      {/* HEADER */}
      <div style={{ backgroundColor: 'white', padding: isMobile ? '100px 20px 40px' : '140px 20px 60px', textAlign: 'center', borderBottom: '1px solid #e2e8f0', boxSizing: 'border-box' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <button 
              onClick={() => navigate('/')} 
              style={{ 
                background: 'none', border: 'none', cursor: 'pointer', 
                display: 'inline-flex', alignItems: 'center', gap: '8px', 
                color: '#64748b', marginBottom: '30px', fontWeight: '500'
              }}
            >
              <ArrowLeft size={16} /> Back to Home
          </button>

          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
            <div style={{ padding: '16px', background: '#e0f2fe', borderRadius: '50%' }}>
              {page.icon}
            </div>
          </div>
          
          <h1 style={{ fontSize: isMobile ? '2.2rem' : '3rem', fontWeight: '800', color: theme.secondary, marginBottom: '20px' }}>
            {page.title}
          </h1>
          <p style={{ fontSize: isMobile ? '1.1rem' : '1.25rem', color: '#64748b', maxWidth: '600px', margin: '0 auto' }}>
            {page.subtitle}
          </p>
        </div>
      </div>

      {/* CONTENT BODY */}
      <div style={{ flex: 1, padding: isMobile ? '40px 20px' : '80px 20px', maxWidth: '800px', margin: '0 auto', width: '100%', boxSizing: 'border-box', lineHeight: '1.8', color: '#334155', fontSize: '1.1rem' }}>
        {page.content}
      </div>

      <Footer theme={theme} />
    </div>
  );
};

export default CompanyPage;